import { Button, DialogRoot, DialogTrigger } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { XCircleIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UserResponse, useUsers } from '@/entities/users';
import { IsActiveBadge } from '@/features/IsActiveBadge';
import { NestedItemCard } from '@/features/nested-items';
import { getShortName } from '@/shared/lib';
import { UserGroupFromValues } from '../UsersGroupForm/types';
import { AddUsersDialog } from './AddUsersDialog';

type Props = {
  disabled?: boolean;
};

export const UsersList: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation(['users', 'common']);
  const { setValue, getValues, watch } = useFormContext<UserGroupFromValues>();
  const fields = watch('users');

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Запрос для получения полных данных всех пользователей
  const { data: usersData } = useUsers({ limit: 100 });
  const allUsers = usersData?.users ?? [];

  const currentUsers = watch('users');

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCancelDialog = () => setIsDialogOpen(false);

  // Состав группы — такое же изменение карточки, как правка её поля: без `shouldDirty` кнопка сохранения остаётся неактивной
  const handleSaveDialog = (newUsers: UserResponse[]) => {
    // Сохраняем только минимальные данные (id)
    const newUserIds = newUsers.map((user) => ({ id: user.id }));
    setValue('users', [...(getValues('users') || []), ...newUserIds], { shouldDirty: true });
    setIsDialogOpen(false);
  };

  const handleRemoveUser = (id: number) => {
    setValue('users', getValues('users')?.filter((user) => user.id !== id) || [], { shouldDirty: true });
  };

  // Поиск полных данных по идентификатору
  const getUserData = (id: number): UserResponse | undefined => {
    return allUsers.find((user) => user.id === id);
  };

  const renderUserTitle = (user: UserResponse) => {
    return (
      <span>
        {getShortName(user.firstName, user.lastName, user.middleName)}
        {user.role?.name && <span className="text-sm text-muted-foreground ml-2">{user.role.name}</span>}
        <IsActiveBadge isActive={user.isActive} className="ml-3" />
      </span>
    );
  };

  return (
    <DialogRoot open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <div className="flex flex-col gap-4">
        <div className="flex gap-5 items-center">
          <p className="font-medium">{t('users')}</p>

          {!disabled && (
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" className="ml-auto" onClick={handleOpenDialog}>
                {t('addUser')}
              </Button>
            </DialogTrigger>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {fields?.map((field) => {
            const userData = getUserData(field.id);
            if (!userData) return null;
            return (
              <NestedItemCard
                key={userData.id}
                item={userData}
                renderTitle={renderUserTitle}
                renderDescription={(user) => user.email}
                action={
                  !disabled && (
                    <IconButton
                      icon={<XCircleIcon />}
                      variant="ghost"
                      color="destructive"
                      label={t('users:removeUserFromGroup')}
                      onClick={() => handleRemoveUser(field.id)}
                    />
                  )
                }
              />
            );
          })}
          {fields?.length === 0 && <p className="text-sm text-muted-foreground">{t('noUsersInGroup')}</p>}
        </div>
      </div>

      <AddUsersDialog
        existingUserIds={currentUsers?.map((user) => user.id) || []}
        onCancel={handleCancelDialog}
        onSave={handleSaveDialog}
        className="max-w-160"
      />
    </DialogRoot>
  );
};
